/* eslint-disable complexity */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';

import { toastError, UILoader } from '@futrli/ui-components';
import {
  auth0Authenticator,
  APIClients,
  Logger,
  ROUTES,
  ApiClientType,
  getSageIdAdditionalInfoUrl,
  API_PATHS,
  IntegrationsAPIClient,
  checkCurrentUser,
  updateCurrentUser,
  QUERY_PARAMS,
  getOrganisationsSettingsUrl,
  isSetupCompleted,
  getHomePageUrl,
  getSfaBusinessId,
  SfaSetupNotCompleteFatalError,
  removeSfaBusinessId,
  getSfaOnboardingUrl,
  triggerLoginSuccessEvent
} from '@futrli/shared-services';
import { ERROR_STATUS } from '@futrli/errors';

import { getInvitationInviteReference, removeInvitationData } from '../utils/invite';
import { setSageIdUser } from './redux-utils/actions';
import { getSage50Data, removeSage50Data, storeSage50OrgData } from '../utils/sage-50';
import { fetchWorkspacesData } from '../../../skeleton/src/workspaces/api';
import { getSage50Org, getSage50Workspace } from './utils/utils';

const ACCESS_DENIED_ERROR = 'access_denied';

/**
 * Automatic SSO sage id trigger by url.
 */
export const SageIdSignInTrigger = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isFirstRender = useRef(true);

  const [sfaUserNotFound, setSfaUserNotFound] = useState(false);
  const sfaBusinessId = getSfaBusinessId();
  const sfaOnboardingRedirect = getSfaOnboardingUrl(sfaBusinessId);

  useEffect(() => {
    const getData = async () => {
      try {
        // Setting api clients to Auth0
        APIClients.setApiClient(ApiClientType.Auth0);
        IntegrationsAPIClient.setApiClient(ApiClientType.Auth0);

        // Verifying redirect from SageId auth page
        await auth0Authenticator.handleRedirectCallback();

        // Triggering login update event when user login is successful (sent in background, doesn't need to be awaited)
        triggerLoginSuccessEvent();

        // Fetch current user from backend -> in case of 404 we need to redirect user to Additional Info page
        let currentUser = await checkCurrentUser();

        // SfA onboarding check
        if (sfaBusinessId) {
          removeSfaBusinessId();
          dispatch(setSageIdUser(navigate, false, currentUser, sfaOnboardingRedirect, true));
          return;
        }

        const sage50BusinessId = getSage50Data();

        // Sage 50 integration logic
        if (sage50BusinessId) {
          const workspacesData = await fetchWorkspacesData();
          const sage50Org = getSage50Org(workspacesData, sage50BusinessId);
          // In case of missing Sage50 org, do redirect with appropriate message
          if (!sage50Org) {
            const workspaceId = workspacesData?.[0]?.id;
            removeSage50Data();
            Logger.error('Unable to find sage50 org associated with given business id');

            if (currentUser.setup_complete) {
              dispatch(
                setSageIdUser(
                  navigate,
                  false,
                  currentUser,
                  workspaceId
                    ? `${getOrganisationsSettingsUrl(workspaceId)}?${QUERY_PARAMS.SAGE_50_ERROR}=true`
                    : ROUTES.ROOT_ROUTE,
                  true
                )
              );
            } else {
              dispatch(
                setSageIdUser(
                  navigate,
                  false,
                  currentUser,
                  `${getSageIdAdditionalInfoUrl()}?${QUERY_PARAMS.SAGE_50_ERROR}=true`,
                  false
                )
              );
            }
            return;
          }

          dispatch(setSageIdUser(navigate, false, currentUser, undefined, currentUser.setup_complete));
          if (
            isSetupCompleted({
              ...currentUser,
              setupCompleted: currentUser.setup_complete
            })
          ) {
            removeSage50Data();
            const sage50Workspace = getSage50Workspace(workspacesData, sage50Org.id);
            if (!sage50Workspace) {
              return navigate(ROUTES.ROOT_ROUTE);
            }
            // User already completed initial setup and can be redirected to Home page
            return navigate(getHomePageUrl(sage50Workspace.id, sage50Org.id));
          } else {
            const sage50Workspace = getSage50Workspace(workspacesData, sage50Org.id);
            if (!sage50Workspace) {
              return navigate(ROUTES.ROOT_ROUTE);
            }
            // User didn't complete additional info page and needs to be redirected to Additional info page
            storeSage50OrgData(sage50Workspace.id, sage50Org.id, sage50Org.datasource?.id || '');
            return navigate(getSageIdAdditionalInfoUrl());
          }
        }

        // User has completed setup but there is invitation data, that means
        // user clicked again on already submitted invitation link, we will
        // just normally login in that user as if user submitted login form
        if (currentUser.setup_complete && getInvitationInviteReference()) {
          removeInvitationData();
        }

        // checkCurrentUser route returned 200 and sageid_id is null ->
        // current user is invited user. We need to update sageid_id
        if (!currentUser.sageid_id) {
          const auth0User = await auth0Authenticator.getCurrentAuthenticatedUser(currentUser);
          const currentSageIdId = get(auth0User, 'sageIdId', '');
          const inviteReference = getInvitationInviteReference();
          if (currentSageIdId && inviteReference) {
            currentUser = await updateCurrentUser({ sageid_id: currentSageIdId, inviteReference });
          }
          auth0Authenticator.updateCurrentUser(currentUser);
        }

        // User didn't complete setup (submitting provision request on Additional info page)
        // so we need to redirect that user to Additional info page
        if (!currentUser.setup_complete) {
          dispatch(setSageIdUser(navigate, false, currentUser, undefined, false));
          navigate(getSageIdAdditionalInfoUrl());
          return;
        }

        // User has sageid id and it has connected current user in our backend -> we can let that
        // user into our system without any additional checks
        dispatch(setSageIdUser(navigate, true, currentUser, undefined, true));
      } catch (e) {
        // User declined Sage app authorization
        if (get(e, 'error') === ACCESS_DENIED_ERROR) {
          navigate(ROUTES.LOGIN);
          return;
        }

        const path = (get(e, 'response.config.url') as unknown) as string;
        const errorStatus = get(e, 'response.status');
        // User is not set in our system -> /users/current returns 404
        if (errorStatus === ERROR_STATUS._404 && path.startsWith(`${API_PATHS.USERS_CURRENT}`)) {
          if (sfaBusinessId) {
            // For SFA, we show a special error screen as this usually means the user has not been provisioned yet
            setSfaUserNotFound(true);
            return;
          }

          const sage50BusinessId = getSage50Data();
          // If we have 404 for current user , that means user doesn't exist in our system. If we have businessId and
          // not existing user, we can't proceed with that user (we can't create workspace for this user)
          if (sage50BusinessId) {
            removeSage50Data();
            auth0Authenticator.signOut(true);
            return;
          }
          dispatch(setSageIdUser(navigate, false, undefined, undefined, false));
          navigate(getSageIdAdditionalInfoUrl());
          return;
        }

        toastError();
        Logger.error(e as string);
        navigate(ROUTES.LOGIN);
      }
    };

    if (isFirstRender) {
      isFirstRender.current = false;
      getData();
    }
  }, [dispatch, navigate, sfaBusinessId, sfaOnboardingRedirect]);

  if (sfaUserNotFound) {
    return <SfaSetupNotCompleteFatalError />;
  }

  return <UILoader isFull={true} />;
};
